/*!
 * cron-widget.js — a dependency-free cron expression picker for forms.
 *
 * Binds to an <input>. Initializes its UI from the input's current value
 * (a 5-field cron string like "* * * * *") and writes the resulting cron
 * string back to that input on every change (dispatching 'input' + 'change'
 * so Django / other listeners pick it up). Submit the form as usual.
 *
 * Usage:
 *    <input type="text" name="schedule" id="id_schedule" value="0 9 * * 1">
 *    <script src="cron-widget.js"></script>
 *    <script> CronWidget.attach('#id_schedule'); </script>
 *
 * Options: CronWidget.attach(target, { mode:'simple'|'advanced', preview:true })
 *
 * Fields are: minute hour day-of-month month day-of-week.
 */
(function () {
  "use strict";

  /* ---------------- cron engine ---------------- */
  var MONTHS = {jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12};
  var DOWS   = {sun:0,mon:1,tue:2,wed:3,thu:4,fri:5,sat:6};

  function nameToNum(tok, names) {
    var t = String(tok).trim().toLowerCase();
    if (names && names[t] !== undefined) return names[t];
    var n = parseInt(t, 10);
    if (isNaN(n)) throw new Error("bad token: " + tok);
    return n;
  }
  function parseField(expr, min, max, names) {
    var set = new Set();
    String(expr).split(",").forEach(function (raw) {
      var part = raw.trim();
      if (part === "") throw new Error("empty field");
      var step = 1, range = part;
      if (part.indexOf("/") > -1) {
        var bits = part.split("/"); range = bits[0]; step = parseInt(bits[1], 10);
        if (!step || step < 1) throw new Error("bad step");
      }
      var lo, hi;
      if (range === "*") { lo = min; hi = max; }
      else if (range.indexOf("-") > -1) { var ab = range.split("-"); lo = nameToNum(ab[0], names); hi = nameToNum(ab[1], names); }
      else { lo = hi = nameToNum(range, names); if (step > 1) hi = max; }
      if (lo < min || hi > max || lo > hi) throw new Error("out of range: " + part);
      for (var v = lo; v <= hi; v += step) set.add(v);
    });
    return set;
  }
  function parseCron(str) {
    var f = String(str).trim().split(/\s+/);
    if (f.length !== 5) throw new Error("cron needs 5 fields");
    var minute = parseField(f[0], 0, 59, null);
    var hour = parseField(f[1], 0, 23, null);
    var dom = parseField(f[2], 1, 31, null);
    var month = parseField(f[3], 1, 12, MONTHS);
    var dow = parseField(f[4], 0, 7, DOWS);
    if (dow.has(7)) { dow.add(0); dow.delete(7); }
    return { minute:minute, hour:hour, dom:dom, month:month, dow:dow,
             domRestricted: f[2].trim() !== "*", dowRestricted: f[4].trim() !== "*" };
  }
  // The job engine evaluates cron fields against naive server time, which is
  // effectively UTC. So matching must be done in UTC too, or
  // this preview won't agree with when the job 1 runs.
  function matches(d, f) {
    if (!f.month.has(d.getUTCMonth() + 1)) return false;
    if (!f.hour.has(d.getUTCHours())) return false;
    if (!f.minute.has(d.getUTCMinutes())) return false;
    var domOk = f.dom.has(d.getUTCDate()), dowOk = f.dow.has(d.getUTCDay());
    if (f.domRestricted && f.dowRestricted) return domOk || dowOk;
    if (f.domRestricted) return domOk;
    if (f.dowRestricted) return dowOk;
    return true;
  }
  function nextRuns(str, count) {
    var f = parseCron(str), out = [], d = new Date();
    d.setUTCSeconds(0, 0); d.setUTCMinutes(d.getUTCMinutes() + 1);
    var guard = 0, limit = 527040 * 2;
    while (out.length < count && guard < limit) {
      if (matches(d, f)) out.push(new Date(d));
      d.setUTCMinutes(d.getUTCMinutes() + 1); guard++;
    }
    return out;
  }
  function isValidCron(str) { try { parseCron(str); return true; } catch (e) { return false; } }

  function pad2(i){ return String(i).padStart(2, "0"); }

  /* ---------------- schedule model <-> cron ---------------- */
  function buildExpr(j) {
    var m = j.minute, h = j.hour;
    switch (j.freq) {
      case "minutes":  return (j.every > 1 ? "*/" + j.every : "*") + " * * * *";
      case "hours":    return m + " " + (j.every > 1 ? "*/" + j.every : "*") + " * * *";
      case "daily":    return m + " " + h + " * * *";
      case "weekdays": return m + " " + h + " * * 1-5";
      case "weekly":   return m + " " + h + " * * " + j.dow;
      case "monthly":  return m + " " + h + " " + j.dom + " * *";
      case "yearly":   return m + " " + h + " " + j.dom + " " + j.month + " *";
      default:         return "* * * * *";
    }
  }

  // Decode a cron string into the friendly model when it matches a known shape.
  function decode(str) {
    if (!isValidCron(str)) return { ok: false };
    var p = str.trim().split(/\s+/), M = p[0], H = p[1], D = p[2], MO = p[3], W = p[4];
    var isInt = function (s) { return /^\d+$/.test(s); };
    var stepOf = function (s) { var m = /^\*\/(\d+)$/.exec(s); return m ? +m[1] : null; };

    if (H === "*" && D === "*" && MO === "*" && W === "*") {
      if (M === "*") return { ok: true, freq: "minutes", every: 1 };
      if (stepOf(M)) return { ok: true, freq: "minutes", every: stepOf(M) };
      if (isInt(M)) return { ok: true, freq: "hours", every: 1, minute: +M };   // m * * * *  => hourly at :m
    }
    if (isInt(M) && D === "*" && MO === "*" && W === "*") {
      if (stepOf(H)) return { ok: true, freq: "hours", every: stepOf(H), minute: +M };
      if (isInt(H)) return { ok: true, freq: "daily", minute: +M, hour: +H };
    }
    if (isInt(M) && isInt(H) && D === "*" && MO === "*") {
      if (W === "1-5") return { ok: true, freq: "weekdays", minute: +M, hour: +H };
      if (isInt(W)) return { ok: true, freq: "weekly", minute: +M, hour: +H, dow: +W };
    }
    if (isInt(M) && isInt(H) && isInt(D) && MO === "*" && W === "*")
      return { ok: true, freq: "monthly", minute: +M, hour: +H, dom: +D };
    if (isInt(M) && isInt(H) && isInt(D) && isInt(MO) && W === "*")
      return { ok: true, freq: "yearly", minute: +M, hour: +H, dom: +D, month: +MO };

    return { ok: true, custom: true };  // valid but not a friendly shape
  }

  var SIMPLE_PRESETS = [
    { v:"5min",    label:"Every 5 minutes",   freq:"minutes", every:5 },
    { v:"15min",   label:"Every 15 minutes",  freq:"minutes", every:15 },
    { v:"30min",   label:"Every 30 minutes",  freq:"minutes", every:30 },
    { v:"hourly",  label:"Hourly",            freq:"hours",   every:1 },
    { v:"2h",      label:"Every 2 hours",     freq:"hours",   every:2 },
    { v:"3h",      label:"Every 3 hours",     freq:"hours",   every:3 },
    { v:"6h",      label:"Every 6 hours",     freq:"hours",   every:6 },
    { v:"12h",     label:"Twice a day (12h)", freq:"hours",   every:12 },
    { v:"daily",   label:"Daily",             freq:"daily" },
    { v:"weekdays",label:"Weekdays (Mon–Fri)", freq:"weekdays" },
    { v:"weekly",  label:"Weekly",            freq:"weekly" },
    { v:"monthly", label:"Monthly",           freq:"monthly" }
  ];
  function presetOf(j) {
    for (var i = 0; i < SIMPLE_PRESETS.length; i++) {
      var p = SIMPLE_PRESETS[i];
      if (p.freq !== j.freq) continue;
      if (p.freq === "minutes" || p.freq === "hours") { if (p.every === j.every) return p.v; }
      else return p.v;
    }
    return null;
  }

  var MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  var DOW_NAMES = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  function opts(n0, n1, fmtFn) { var h = ""; for (var i = n0; i <= n1; i++) h += '<option value="' + i + '">' + (fmtFn ? fmtFn(i) : i) + "</option>"; return h; }

  /* ---------------- styles (injected once) ---------------- */
  // Colors use Bootstrap 5 CSS variables (--bs-*) with fallbacks, so the
  // widget picks up the app's theme (including branded portal overrides)
  // and degrades gracefully on legacy pages where those vars aren't defined.
  var CSS = '' +
  '.cw{font:14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:var(--bs-body-color,#1f2733);max-width:640px}' +
  '.cw *{box-sizing:border-box}' +
  '.cw-top{display:flex;align-items:center;gap:10px;margin-bottom:10px;flex-wrap:wrap}' +
  '.cw-b{display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-bottom:8px}' +
  // Bootstrap's .form-control/.form-select set width:100% which would break
  // the inline flex layout — force w-auto so controls sit next to their labels.
  '.cw .form-control,.cw .form-select{width:auto}' +
  '.cw .form-control[type=number]{width:64px}' +
  '.cw .lbl{color:var(--bs-secondary-color,#6b7688);font-size:13px}' +
  '.cw-raw{display:flex;gap:8px;align-items:center;margin:8px 0 4px}' +
  '.cw-raw input{flex:1;font-family:ui-monospace,SFMono-Regular,Menlo,monospace}' +
  '.cw-raw input.bad{border-color:var(--bs-danger,#e05555);background:var(--bs-danger-bg-subtle,#fff5f5)}' +
  '.cw-expr{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:13px;background:var(--bs-dark,#0f172a);color:var(--bs-light,#e6edf3);padding:5px 9px;border-radius:7px;display:inline-block}' +
  '.cw-next{font-size:12.5px;color:var(--bs-secondary-color,#6b7688);margin-top:8px}' +
  '.cw-next b{color:var(--bs-body-color,#1f2733)}.cw-next ul{margin:3px 0 0;padding-left:16px}.cw-next li{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12px}' +
  '.cw-note{font-size:12.5px;color:var(--bs-secondary-color,#6b7688)}';
  var cssInjected = false;
  function injectCSS() { if (cssInjected) return; var s = document.createElement("style"); s.textContent = CSS; document.head.appendChild(s); cssInjected = true; }

  /* ---------------- widget instance ---------------- */
  function Widget(input, opts_) {
    this.input = input;
    this.opts = opts_ || {};
    this.mode = this.opts.mode || "simple";
    this.preview = this.opts.preview !== false;
    this.model = { freq:"daily", every:2, minute:0, hour:9, dom:1, month:1, dow:1 };
    this.custom = false;
    this.rawValue = "* * * * *";

    injectCSS();
    input.style.display = "none";
    this.el = document.createElement("div");
    this.el.className = "cw";
    input.insertAdjacentElement("afterend", this.el);

    this.setFromCron(input.value && input.value.trim() ? input.value.trim() : (this.opts.initial || "* * * * *"));
    // If the initial value can't be expressed with the Simple presets, open in Advanced.
    if (this.mode === "simple" && !this.fitsSimple()) this.mode = "advanced";
    this.render();
  }
  // True when the current model maps to a Simple-mode preset (so Simple can represent it).
  Widget.prototype.fitsSimple = function () {
    return !this.custom && presetOf(this.model) !== null;
  };
  // Public: load a new cron value and re-pick the mode (Simple if it fits, else Advanced).
  Widget.prototype.setValue = function (cron) {
    this.setFromCron(String(cron).trim());
    this.mode = this.fitsSimple() ? "simple" : "advanced";
    this.render();
  };
  Widget.prototype.setFromCron = function (str) {
    var d = decode(str);
    if (!d.ok) { this.custom = true; this.rawValue = str; return; }
    if (d.custom) { this.custom = true; this.rawValue = str.trim(); return; }
    this.custom = false;
    this.model.freq = d.freq;
    if (d.every !== undefined) this.model.every = d.every;
    if (d.minute !== undefined) this.model.minute = d.minute;
    if (d.hour !== undefined) this.model.hour = d.hour;
    if (d.dom !== undefined) this.model.dom = d.dom;
    if (d.month !== undefined) this.model.month = d.month;
    if (d.dow !== undefined) this.model.dow = d.dow;
    this.rawValue = buildExpr(this.model);
  };
  Widget.prototype.value = function () { return this.custom ? this.rawValue : buildExpr(this.model); };
  Widget.prototype.commit = function () {
    var v = this.value();
    this.input.value = v;
    this.input.dispatchEvent(new Event("input", { bubbles: true }));
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
  };

  Widget.prototype.render = function () {
    var j = this.model;
    var simpleCls = this.mode === "simple" ? "btn-primary" : "btn-outline-primary";
    var advancedCls = this.mode === "advanced" ? "btn-primary" : "btn-outline-primary";
    var html = '<div class="cw-top"><div class="btn-group btn-group-sm cw-seg" role="group" aria-label="Editor mode">' +
      '<button type="button" data-mode="simple" class="btn ' + simpleCls + '">Simple</button>' +
      '<button type="button" data-mode="advanced" class="btn ' + advancedCls + '">Advanced</button>' +
      '</div></div>';

    if (this.mode === "simple") {
      html += '<div class="cw-b">' + this.simpleBuilder() + "</div>";
    } else {
      html += '<div class="cw-b">' + this.advancedBuilder() + "</div>";
      html += '<div class="cw-raw"><span class="lbl">cron</span>' +
              '<input type="text" class="form-control form-control-sm" data-role="raw" value="' + this.value() + '" spellcheck="false"></div>';
    }
    if (this.preview) html += '<div class="cw-next" data-role="next"></div>';
    this.el.innerHTML = html;

    // reflect select values
    this.el.querySelectorAll("select[data-k]").forEach(function (s) {
      var k = s.getAttribute("data-k"); if (j[k] !== undefined) s.value = j[k];
    });
    var adv = this.el.querySelector("select[data-k=freq]"); if (adv) adv.value = j.freq;
    var pre = this.el.querySelector("[data-simple=preset]");
    if (pre) pre.value = this.custom ? "__custom" : (presetOf(j) || "__custom");

    this.wire();
    this.updateMeta();
    this.commit();
  };

  // Bootstrap 5 classes so the controls take on the app's theme (borders,
  // focus rings, background) instead of the browser's native chrome.
  var SEL_CLS = 'class="form-select form-select-sm"';
  var CTL_CLS = 'class="form-control form-control-sm"';

  // Simple and advance builders assume the time values are in UTC.
  Widget.prototype.simpleBuilder = function () {
    var j = this.model, pv = this.custom ? null : presetOf(j);
    var sel = '<span class="lbl">Frequency</span><select ' + SEL_CLS + ' data-simple="preset">';
    SIMPLE_PRESETS.forEach(function (p) { sel += '<option value="' + p.v + '"' + (pv===p.v?" selected":"") + ">" + p.label + "</option>"; });
    if (pv === null) sel += '<option value="__custom" selected>Custom (see Advanced)</option>';
    sel += "</select>";
    var extra = "", tv = pad2(j.hour)+":"+pad2(j.minute);
    if (this.custom) extra = '<span class="cw-note">Custom expression — switch to Advanced to edit.</span>';
    else if (j.freq === "daily" || j.freq === "weekdays")
      extra = '<span class="lbl">at</span><input type="time" ' + CTL_CLS + ' data-time="1" value="'+tv+'"><span class="lbl">UTC</span>';
    else if (j.freq === "weekly")
      extra = '<span class="lbl">on</span><select ' + SEL_CLS + ' data-k="dow">'+opts(0,6,function(i){return DOW_NAMES[i];})+'</select><span class="lbl">at</span><input type="time" ' + CTL_CLS + ' data-time="1" value="'+tv+'"><span class="lbl">UTC</span>';
    else if (j.freq === "monthly")
      extra = '<span class="lbl">on day</span><select ' + SEL_CLS + ' data-k="dom">'+opts(1,31)+'</select><span class="lbl">at</span><input type="time" ' + CTL_CLS + ' data-time="1" value="'+tv+'"><span class="lbl">UTC</span>';
    return sel + extra;
  };

  Widget.prototype.advancedBuilder = function () {
    var j = this.model, tv = pad2(j.hour)+":"+pad2(j.minute);
    var s = '<span class="lbl">Run</span><select ' + SEL_CLS + ' data-k="freq">' +
      '<option value="minutes">every N minutes</option>' +
      '<option value="hours">every N hours</option>' +
      '<option value="daily">daily</option>' +
      '<option value="weekly">weekly</option>' +
      '<option value="monthly">monthly</option>' +
      '<option value="yearly">yearly</option></select>';
    if (this.custom) return s + '<span class="cw-note">Custom — edit the cron field below, or pick a frequency to use the builder.</span>';
    if (j.freq === "minutes")
      s += '<span class="lbl">every</span><input type="number" ' + CTL_CLS + ' min="1" max="59" data-k="every" value="'+j.every+'"><span class="lbl">minute(s)</span>';
    else if (j.freq === "hours")
      s += '<span class="lbl">every</span><input type="number" ' + CTL_CLS + ' min="1" max="23" data-k="every" value="'+j.every+'"><span class="lbl">hour(s), at minute</span><select ' + SEL_CLS + ' data-k="minute">'+opts(0,59,pad2)+'</select>';
    else if (j.freq === "daily")
      s += '<span class="lbl">at</span><input type="time" ' + CTL_CLS + ' data-time="1" value="'+tv+'"><span class="lbl">UTC</span>';
    else if (j.freq === "weekly")
      s += '<span class="lbl">on</span><select ' + SEL_CLS + ' data-k="dow">'+opts(0,6,function(i){return DOW_NAMES[i];})+'</select><span class="lbl">at</span><input type="time" ' + CTL_CLS + ' data-time="1" value="'+tv+'"><span class="lbl">UTC</span>';
    else if (j.freq === "monthly")
      s += '<span class="lbl">on day</span><select ' + SEL_CLS + ' data-k="dom">'+opts(1,31)+'</select><span class="lbl">at</span><input type="time" ' + CTL_CLS + ' data-time="1" value="'+tv+'"><span class="lbl">UTC</span>';
    else if (j.freq === "yearly")
      s += '<span class="lbl">in</span><select ' + SEL_CLS + ' data-k="month">'+opts(1,12,function(i){return MONTH_NAMES[i-1];})+'</select><span class="lbl">on day</span><select ' + SEL_CLS + ' data-k="dom">'+opts(1,31)+'</select><span class="lbl">at</span><input type="time" ' + CTL_CLS + ' data-time="1" value="'+tv+'"><span class="lbl">UTC</span>';
    return s;
  };

  Widget.prototype.wire = function () {
    var self = this, j = this.model;
    this.el.querySelectorAll(".cw-seg button").forEach(function (b) {
      b.addEventListener("click", function () { self.mode = b.getAttribute("data-mode"); self.render(); });
    });
    this.el.querySelectorAll("[data-k]").forEach(function (inp) {
      var k = inp.getAttribute("data-k");
      var evt = (inp.tagName === "SELECT") ? "change" : "input";
      inp.addEventListener(evt, function () {
        if (inp.type === "number") j[k] = Math.max(1, parseInt(inp.value || "1", 10));
        else if (inp.tagName === "SELECT" && k !== "freq") j[k] = parseInt(inp.value, 10);
        else j[k] = inp.value; // freq
        if (k === "freq") { self.custom = false; self.render(); }
        else { self.rawValue = buildExpr(j); self.updateMeta(); self.commit(); self.syncRaw(); }
      });
    });
    var pre = this.el.querySelector("[data-simple=preset]");
    if (pre) pre.addEventListener("change", function () {
      var p = SIMPLE_PRESETS.filter(function (x) { return x.v === pre.value; })[0];
      if (p) { self.custom = false; j.freq = p.freq; if (p.every !== undefined) j.every = p.every; }
      self.render();
    });
    var t = this.el.querySelector("input[data-time]");
    if (t) t.addEventListener("input", function () {
      var hm = t.value.split(":"); var hh = parseInt(hm[0],10), mm = parseInt(hm[1],10);
      if (!isNaN(hh)) j.hour = hh; if (!isNaN(mm)) j.minute = mm;
      self.rawValue = buildExpr(j); self.updateMeta(); self.commit();
    });
    var raw = this.el.querySelector("[data-role=raw]");
    if (raw) raw.addEventListener("input", function () {
      if (isValidCron(raw.value)) {
        raw.classList.remove("bad");
        self.setFromCron(raw.value.trim());
        // update friendly controls without nuking the raw field focus:
        self.refreshControls(); self.updateMeta(); self.commit();
      } else { raw.classList.add("bad"); self.updateMeta(raw.value); }
    });
  };

  // Re-sync the friendly builder area after a raw edit (keeps raw input focused).
  Widget.prototype.refreshControls = function () {
    var b = this.el.querySelector(".cw-b"); if (!b) return;
    b.innerHTML = this.mode === "simple" ? this.simpleBuilder() : this.advancedBuilder();
    var j = this.model;
    b.querySelectorAll("select[data-k]").forEach(function (s) { var k = s.getAttribute("data-k"); if (j[k] !== undefined) s.value = j[k]; });
    var adv = b.querySelector("select[data-k=freq]"); if (adv) adv.value = j.freq;
    var pre = b.querySelector("[data-simple=preset]"); if (pre) pre.value = this.custom ? "__custom" : (presetOf(j) || "__custom");
    // re-wire just this area
    var self = this;
    b.querySelectorAll("[data-k]").forEach(function (inp) {
      var k = inp.getAttribute("data-k"); var evt = (inp.tagName === "SELECT") ? "change" : "input";
      inp.addEventListener(evt, function () {
        if (inp.type === "number") j[k] = Math.max(1, parseInt(inp.value || "1", 10));
        else if (inp.tagName === "SELECT" && k !== "freq") j[k] = parseInt(inp.value, 10);
        else j[k] = inp.value;
        if (k === "freq") { self.custom = false; self.render(); }
        else { self.rawValue = buildExpr(j); self.updateMeta(); self.commit(); self.syncRaw(); }
      });
    });
    var pre2 = b.querySelector("[data-simple=preset]");
    if (pre2) pre2.addEventListener("change", function () {
      var p = SIMPLE_PRESETS.filter(function (x) { return x.v === pre2.value; })[0];
      if (p) { self.custom = false; j.freq = p.freq; if (p.every !== undefined) j.every = p.every; }
      self.render();
    });
    var t = b.querySelector("input[data-time]");
    if (t) t.addEventListener("input", function () {
      var hm = t.value.split(":"); var hh = parseInt(hm[0],10), mm = parseInt(hm[1],10);
      if (!isNaN(hh)) j.hour = hh; if (!isNaN(mm)) j.minute = mm;
      self.rawValue = buildExpr(j); self.updateMeta(); self.commit();
    });
  };

  Widget.prototype.syncRaw = function () {
    var raw = this.el.querySelector("[data-role=raw]"); if (raw) raw.value = this.value();
  };

  Widget.prototype.updateMeta = function (overrideStr) {
    var expr = overrideStr !== undefined ? overrideStr : this.value();
    var exprEl = this.el.querySelector("[data-role=expr]"); if (exprEl) exprEl.textContent = expr;
    var nextEl = this.el.querySelector("[data-role=next]"); if (!nextEl) return;
    try {
      var runs = nextRuns(expr, 3);
      nextEl.innerHTML = "<b>Next runs (local time):</b> " + runs.map(function (r) { return c2.when.renderWithTooltip(r); }).join(", ");
      if (c2.tooltips) c2.tooltips.init(nextEl);
    } catch (e) {
      nextEl.innerHTML = '<b style="color:#e05555">Invalid:</b> ' + e.message;
    }
  };

  /* ---------------- public API ---------------- */
  window.CronWidget = {
    attach: function (target, opts) {
      var input = typeof target === "string" ? document.querySelector(target) : target;
      if (!input) throw new Error("CronWidget: target not found: " + target);
      return new Widget(input, opts);
    },
    // exposed helpers in case you want them elsewhere
    isValid: isValidCron,
    nextRuns: nextRuns
  };
})();
